/**
 * Promotions admin page (/promotions, super_admin only).
 *
 * Phase 1 of the APC Commercial Engine: temporary, presentation-only
 * promotions that override a product's price + photos + description in the
 * storefront and during checkout. They never modify the catalog.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, PencilSimple, Trash, MegaphoneSimple, X, Tag,
  CheckCircle, XCircle, Calendar, Image as ImageIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { uploadPhoto } from "@/lib/uploadPhoto";

const EMPTY = {
  product_id: "", name: "", description: "",
  promo_price: "", currency: "MXN", badge_label: "PROMO",
  start_date: "", end_date: "", order_priority: 0, active: true,
  photos: [], tags: [], free_shipping: true,
};

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-MX") : "—";

export default function Promotions() {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [productSearch, setProductSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: promos }, { data: prods }] = await Promise.all([
        api.get("/promotions"),
        api.get("/products"),
      ]);
      setPromotions(promos);
      setProducts(prods);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setProductSearch(""); setShowForm(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      product_id: p.product_id, name: p.name, description: p.description || "",
      promo_price: p.promo_price, currency: p.currency || "MXN",
      badge_label: p.badge_label || "PROMO",
      start_date: p.start_date ? p.start_date.slice(0, 10) : "",
      end_date: p.end_date ? p.end_date.slice(0, 10) : "",
      order_priority: p.order_priority || 0,
      active: p.active,
      photos: p.photos || [], tags: p.tags || [],
      free_shipping: p.free_shipping !== false,
    });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.product_id) return toast.error("Selecciona un producto");
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    if (!form.promo_price || Number(form.promo_price) <= 0) return toast.error("Precio promocional inválido");
    setSaving(true);
    try {
      const payload = {
        product_id: form.product_id, name: form.name.trim(),
        description: form.description.trim(),
        promo_price: Number(form.promo_price),
        currency: form.currency || "MXN",
        badge_label: form.badge_label || "PROMO",
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        order_priority: Number(form.order_priority || 0),
        active: !!form.active,
        photos: form.photos.filter(Boolean),
        tags: form.tags.filter(Boolean),
        free_shipping: !!form.free_shipping,
      };
      if (editingId) {
        const { product_id: _, ...patch } = payload;
        await api.patch(`/promotions/${editingId}`, patch);
      } else {
        await api.post("/promotions", payload);
      }
      toast.success(editingId ? "Promoción actualizada" : "Promoción creada");
      setShowForm(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar la promoción "${p.name}"?`)) return;
    try {
      await api.delete(`/promotions/${p.id}`);
      toast.success("Promoción eliminada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error eliminando");
    }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/promotions/${p.id}`, { active: !p.active });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const addPhoto = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Máx 8 MB");
    try {
      const url = await uploadPhoto(file, { folder: "alendrapc/promotions", maxSide: 1600, quality: 0.85 });
      setForm((f) => ({ ...f, photos: [...f.photos, url] }));
    } catch (err) { toast.error(err?.message || "No se pudo subir la imagen"); }
  };
  const removePhoto = (i) => setForm((f) => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    setForm((f) => ({ ...f, tags: [...new Set([...f.tags, t])] }));
    setTagInput("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));

  const filteredProducts = productSearch.trim()
    ? products.filter((p) => (p.name + " " + (p.brand || "") + " " + (p.sku || "")).toLowerCase().includes(productSearch.toLowerCase()))
    : products.slice(0, 25);

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">MOTOR COMERCIAL · FASE 1</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight inline-flex items-center gap-3">
            <MegaphoneSimple size={32} weight="duotone" className="text-[#E0000F]" />
            Promociones
          </h1>
          <p className="text-zinc-500 mt-1 text-sm">
            {promotions.length} promociones · {promotions.filter((p) => p.live).length} activas en este momento
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2 bg-[#E0000F] hover:bg-[#B0000C] text-white" data-testid="create-promotion">
          <Plus size={14} weight="bold" /> Nueva promoción
        </Button>
      </header>

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="promotions-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left px-4 py-3">Promoción</th>
              <th className="text-left px-4 py-3">Producto</th>
              <th className="text-left px-4 py-3">Original</th>
              <th className="text-left px-4 py-3">Promo</th>
              <th className="text-left px-4 py-3">Descuento</th>
              <th className="text-left px-4 py-3">Vigencia</th>
              <th className="text-left px-4 py-3">Estado</th>
              <th className="text-right px-4 py-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400">Cargando…</td></tr>}
            {!loading && promotions.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-16 text-center text-zinc-400 dotted-bg">Sin promociones aún. Crea la primera.</td></tr>
            )}
            {!loading && promotions.map((p) => {
              const orig = p.original_price_snapshot || 0;
              const discount = orig > 0 ? Math.round((1 - p.promo_price / orig) * 100) : 0;
              return (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`promotion-row-${p.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-semibold">{p.name}</div>
                    <span className="inline-block mt-0.5 px-2 py-0.5 text-[10px] font-bold rounded bg-[#E0000F]/10 text-[#E0000F]">{p.badge_label}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-700">{p.product?.name || p.product_name_snapshot || "(eliminado)"}</td>
                  <td className="px-4 py-3 text-zinc-500 line-through numeric">{fmtMXN(orig)}</td>
                  <td className="px-4 py-3 font-bold text-[#E0000F] numeric">{fmtMXN(p.promo_price)}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">-{discount}%</span></td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {p.start_date && <div>desde {fmtDate(p.start_date)}</div>}
                    {p.end_date && <div className={new Date(p.end_date) < new Date() ? "text-red-600 font-semibold" : ""}>hasta {fmtDate(p.end_date)}</div>}
                    {!p.start_date && !p.end_date && "Sin vencimiento"}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActive(p)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-semibold ${p.live ? "bg-emerald-50 text-emerald-700" : p.active ? "bg-amber-50 text-amber-700" : "bg-zinc-100 text-zinc-500"}`}
                      data-testid={`toggle-${p.id}`}
                      title={p.live ? "Activa y vigente" : p.active ? "Activa pero fuera de fechas" : "Inactiva"}
                    >
                      {p.live ? <><CheckCircle size={11} weight="fill" /> En vivo</> : p.active ? "Fuera de fechas" : <><XCircle size={11} weight="fill" /> Inactiva</>}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => openEdit(p)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-${p.id}`}>
                      <PencilSimple size={14} weight="bold" />
                    </button>
                    <button onClick={() => remove(p)} className="p-1.5 hover:bg-red-50 text-red-600 rounded ml-1" data-testid={`delete-${p.id}`}>
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="promotion-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{editingId ? "Editar promoción" : "Nueva promoción"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Product picker */}
            {!editingId && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Producto *</Label>
                <Input
                  placeholder="Buscar producto…"
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  className="mt-1 mb-2"
                  data-testid="product-search"
                />
                <div className="border border-zinc-200 rounded max-h-44 overflow-y-auto bg-zinc-50">
                  {filteredProducts.length === 0 && <div className="p-3 text-xs text-zinc-400">Sin resultados</div>}
                  {filteredProducts.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setForm({ ...form, product_id: p.id, promo_price: p.store_price ? Math.round(p.store_price * 0.9) : "" })}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-white transition ${form.product_id === p.id ? "bg-white border-l-4 border-[#E0000F]" : ""}`}
                      data-testid={`product-option-${p.id}`}
                    >
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-zinc-500">{p.brand} · {fmtMXN(p.store_price)} {p.sale_currency}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre de la promoción *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Black Friday especial" className="mt-1" data-testid="promo-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Etiqueta (badge)</Label>
                <Input value={form.badge_label} onChange={(e) => setForm({ ...form, badge_label: e.target.value.toUpperCase() })} placeholder="PROMO · OFERTA · -20%" className="mt-1 font-bold" maxLength={24} data-testid="promo-badge" />
                <p className="text-[10px] text-zinc-500 mt-1">Texto corto que aparece arriba del producto en la tienda.</p>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Texto que reemplaza al del producto (opcional)" className="mt-1 resize-none" data-testid="promo-description" />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Precio promocional *</Label>
                <Input type="number" min="0" step="50" value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} className="mt-1 numeric text-lg font-bold text-[#E0000F]" data-testid="promo-price" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Moneda</Label>
                <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className="mt-1 w-full h-9 border border-zinc-300 rounded px-2 text-sm" data-testid="promo-currency">
                  <option value="MXN">MXN</option>
                  <option value="USD">USD</option>
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Prioridad</Label>
                <Input type="number" min="0" value={form.order_priority} onChange={(e) => setForm({ ...form, order_priority: e.target.value })} className="mt-1 numeric" data-testid="promo-priority" />
                <p className="text-[10px] text-zinc-500 mt-1">Mayor = se muestra primero.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><Calendar size={12} weight="bold" /> Desde</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" data-testid="promo-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Hasta</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1" data-testid="promo-end" />
              </div>
            </div>

            {/* Photos */}
            <div>
              <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><ImageIcon size={12} weight="bold" /> Fotos independientes</Label>
              <p className="text-[10px] text-zinc-500 mb-2">Opcional. Si las dejas vacías, la promoción usa las fotos del producto.</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {form.photos.map((url, i) => (
                  <div key={i} className="relative w-20 h-20 border border-zinc-200 rounded overflow-hidden group" data-testid={`promo-photo-${i}`}>
                    <img src={url} alt="" className="w-full h-full object-contain" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 inline-flex items-center justify-center" data-testid={`remove-photo-${i}`}>
                      <X size={10} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => addPhoto(e.target.files?.[0])} className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#002FA7] file:text-white file:rounded file:cursor-pointer file:hover:bg-[#002277]" data-testid="promo-photo-upload" />
            </div>

            {/* Tags */}
            <div>
              <Label className="text-xs uppercase tracking-wider inline-flex items-center gap-1"><Tag size={12} weight="bold" /> Etiquetas</Label>
              <div className="flex flex-wrap gap-1 mb-2">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 bg-zinc-100 px-2 py-0.5 rounded text-xs">
                    {t}
                    <button type="button" onClick={() => removeTag(t)} className="text-zinc-500 hover:text-red-600"><X size={10} weight="bold" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Agregar etiqueta…" className="text-xs" data-testid="tag-input" />
                <Button type="button" variant="outline" onClick={addTag} className="text-xs">Agregar</Button>
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4" data-testid="promo-active" />
              <span className="text-sm">Promoción activa (debe estar también dentro de fechas para aparecer en la tienda)</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer p-2.5 rounded-md border border-emerald-200 bg-emerald-50/40">
              <input
                type="checkbox"
                checked={!!form.free_shipping}
                onChange={(e) => setForm({ ...form, free_shipping: e.target.checked })}
                className="w-4 h-4 accent-emerald-600"
                data-testid="promo-free-shipping"
              />
              <span className="text-sm">
                <span className="text-emerald-700 font-semibold">🚚 Envío gratis</span>
                <span className="text-zinc-500"> — Mostrará el badge "Envío gratis" en la tienda.</span>
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#E0000F] hover:bg-[#B0000C] text-white" data-testid="save-promotion">
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear promoción"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
